import React, { Component, Fragment } from 'react';
import withAppLayout from '../components/appLayout';
import { Router, Link } from '../configs/routes';
import cx from 'classnames';
import styles from '../styles/compareFunds.scss';
import { Api } from '../api';
import HomeSearch from '../components/home/homeSearch';
import { groups } from '../constants/fundComparision';
import Tabs from '../components/ui/tabs';
import { getFormattedDate, formatRupee } from '../utils/commonUtils';
import { CDN_CLRSAVE_URL } from '../constants';
import InvestFlowHandler from '../components/common/investFlowHandler';
import ReactTooltip from 'react-tooltip';
import Field from '../components/ui/field';
import Loader from '../components/ui/Loader';
import { trackAction } from '../utils/trackActions';
import SuggestedFundsModal from '../components/common/suggestedFundModal';

const api = new Api();

interface View {
  label: string;
  value: string;
}

interface State {
  query: string;
  results: any[];
  fundsToCompare: any[];
  selectedTab: string;
  headers: string[];
  selectedFund: object | any;
  startFlow: boolean;
  views: View[];
  investmentProducts: any[];
  isLoading: boolean;
  isinCode: string;
  showInvestmentDeniedModal: boolean;
}

interface FundInfo {
  productId?: number;
  imageUrl?: string;
  isinCode: string;
  enabled?: boolean;
}

class CompareFunds extends Component<any, State> {
  constructor(props: any) {
    super(props);
    this.state = {
      query: '',
      results: [],
      fundsToCompare: [],
      investmentProducts: [],
      selectedTab: 'quick',
      headers: groups[0].headers,
      selectedFund: null,
      startFlow: false,
      isLoading: false,
      isinCode: '',
      views: groups.map((group) => ({ value: group.key, label: group.label })),
      showInvestmentDeniedModal: false,
    };
  }
  public searchFund(value: string) {
    return api.searchFund(value, 0, [], 'clearsave_all_funds').then((res) => {
      this.setState({
        results: res.hits.slice(0, 10),
      });
    });
  }

  private handleFundClick = (e: any) => {
    const productId = e.currentTarget ? e.currentTarget.dataset.productid : e;
    this.handleQueryParam(productId);
  };

  private async addFundToCompare(fundInfo: FundInfo) {
    let fundsData = await api.getFundById(fundInfo.isinCode, 'ISIN');
    const { fundsToCompare } = this.state;
    fundsData = { ...fundsData, fundInfo };
    const hasFund = fundsToCompare.find((fund) => {
      return fund.fundInfo.isinCode === fundInfo.isinCode;
    });
    if (!hasFund) {
      trackAction(
        'added-to-list',
        'compare-funds',
        fundsData.schemaMaster.fundStandardName,
        fundInfo.productId
      );
      fundsToCompare.push(fundsData);
    }
    this.setState({
      fundsToCompare,
    });
  }

  private removeFund = (id: number) => {
    const { fundsToCompare } = this.state;
    this.setState({
      fundsToCompare: fundsToCompare.filter(
        (fund) => fund.fundInfo.isinCode !== id
      ),
    });
  };

  private switchTabs = (e: any) => {
    const { currentTarget } = e;
    const name = currentTarget.getAttribute('data-tab');
    if (name !== this.state.selectedTab) {
      trackAction('filter-view-tabs-click', 'compare-funds', name);
      const headers = groups.find((group) => group.key === name).headers;
      this.setState({
        selectedTab: name,
        headers,
      });
    }
  };

  private changeView = (e: any) => {
    const { value } = e.target;
    if (value !== this.state.selectedTab) {
      trackAction('filter-view-tabs-click', 'compare-funds', value);
      const headers = groups.find((group) => group.key === value).headers;
      this.setState({
        selectedTab: value,
        headers,
      });
    }
  };

  public UNSAFE_componentWillReceiveProps(nextProps: any) {
    const { fundId: oldFundId } = this.props.url.query;
    const { fundId } = nextProps.url.query;
    if (fundId && fundId !== oldFundId) {
      this.setState({
        startFlow: true,
      });
    }
  }

  public componentDidMount() {
    const { query } = this.props.url;
    if (query.isinCodes) {
      this.setState({
        isLoading: true,
      });
      this.handleQueryParam(query.isinCodes);
    }
  }

  private async handleQueryParam(isinCodes: string) {
    const query = isinCodes
      .split(',')
      .filter((isin) => isin.length)
      .reduce((acc, current) => {
        return acc ? `${acc}&isin=${current}` : `isin=${current}`;
      }, '');
    const { investmentProducts } = this.state;
    if (query) {
      const fundDetails = await api.getFundDetails(query, 'ISIN');
      if (fundDetails && fundDetails.length) {
        fundDetails.forEach((fund) => {
          const duplicate = investmentProducts.find(
            (product) => product.productId === fund.productId
          );

          if (!duplicate) {
            investmentProducts.push(fund);
            const { productId, imageUrl, enabled, isinCode } = fund;
            this.addFundToCompare({
              productId,
              isinCode,
              imageUrl,
              enabled,
            });
          }
        });
      } else {
        isinCodes.split(',').forEach((isinCode) => {
          this.addFundToCompare({ isinCode });
        });
      }
    }
    this.setState({
      investmentProducts,
      isLoading: false,
    });
  }

  private getFundNameColumnContent(fundData: any) {
    const { fundInfo, amcBasic, schemaMaster } = fundData;
    const { isin, slug, fundStandardName: fundName } = schemaMaster;
    const { amcSlug } = amcBasic;

    return (
      <div style={{ display: 'flex' }}>
        <figure
          onClick={() => this.removeFund(isin)}
          className={cx(styles['delete-fund'], 'cursor--pointer')}
          data-tip="Remove Fund"
          data-place="bottom"
          data-for="remove-fund-tip"
        >
          <img
            src={`${CDN_CLRSAVE_URL}/ct-frontend/images/delete.svg`}
            alt="delete"
            width="12"
            height="12"
          />
        </figure>
        <ReactTooltip
          effect="solid"
          type="dark"
          id="remove-fund-tip"
          backgroundColor="black"
        />
        <figure className={styles['fund-image']}>
          <img
            height="24"
            width="20"
            src={`${CDN_CLRSAVE_URL}/clearsave${fundInfo.imageUrl}`}
            alt={fundInfo.imageUrl ? fundName : ''}
          />
        </figure>
        <div>
          <Link
            route={`/mutual-funds/${amcSlug}/${slug}/${isin}?ref=compare-funds`}
          >
            <a className="block font--bold font--black font--hover--blue text--md">
              {fundName}
            </a>
          </Link>
          <a
            onClick={() =>
              this.startInvestment(
                fundInfo.productId,
                fundInfo.enabled,
                fundInfo.isinCode
              )
            }
            className="font--orange font--bold text--md cursor--pointer"
          >
            Invest Now
          </a>
        </div>
      </div>
    );
  }

  private startInvestment = (
    id: number,
    enabled: boolean,
    isinCode: string
  ) => {
    if (!enabled || !id) {
      trackAction('invest-now', 'new-funds-invest', '', id);
      return this.setState((prevState) => ({
        ...prevState,
        isinCode,
        showInvestmentDeniedModal: true,
      }));
    }
    const { investmentProducts } = this.state;
    const fundDetails = investmentProducts.find(
      (product) => product.productId === id
    );
    if (fundDetails) {
      this.setState({
        selectedFund: {
          ...fundDetails,
          id: fundDetails.productId,
        },
      });

      const { search } = location;
      const query = search
        ? `${search}&fundId=${fundDetails.productId}`
        : `?fundId=${fundDetails.productId}`;
      Router.pushRoute(`/save/compare-funds${query}`, { shallow: true });
    }
  };

  private safeDisplayValue(value: any, sign: string = '') {
    if (value) {
      return `${value}${sign}`;
    }
    return 'N/A';
  }

  private getQuickViewContent(fundData: any) {
    const { latestNav, returns, amcBasic, riskRatio, schemaMaster } = fundData;
    const { broadCategoryGroup, categoryName } = schemaMaster;
    return (
      <tr className={styles['fund-row']}>
        <td className="text--md">{this.getFundNameColumnContent(fundData)}</td>
        <td className="text--md">{broadCategoryGroup}</td>
        <td className="text--md">{categoryName}</td>
        <td
          className={cx('text--md', {
            'font--green': latestNav.navChangePercent > 0,
            'font--red': latestNav.navChangePercent < 0,
          })}
        >
          {this.safeDisplayValue(`${latestNav.navChangePercent}`, '%')}
        </td>
        <td
          className={cx('text--md', {
            'font--green': returns.return5yr > 0,
            'font--red': returns.return5yr < 0,
          })}
        >
          {this.safeDisplayValue(returns.return5yr, '%')}
        </td>
        <td className="text--md">{amcBasic.firmName}</td>
        <td className="text--md">{riskRatio.riskLevel}</td>
      </tr>
    );
  }

  private getDetailsViewContent(fundData: any) {
    const { latestNav, schemaMaster, variants } = fundData;
    const { exitLoad, expenseRatio, aum, launchDate, isin } = schemaMaster;
    let variant = null;
    if (variants) {
      variant = variants.find((v) => isin === v.isin);
    }
    return (
      <tr className={styles['fund-row']}>
        <td className="text--md">{this.getFundNameColumnContent(fundData)}</td>
        <td className="text--md">
          &#x20B9; {this.safeDisplayValue(latestNav.dayEndNav)}
        </td>
        <td className="text--md">
          {exitLoad ? `${parseFloat(exitLoad[0].Value).toFixed(2)}%` : 'N/A'}
        </td>
        <td className="text--md">{this.safeDisplayValue(expenseRatio)}%</td>
        <td className="text--md">
          &#x20B9; {formatRupee(aum / 10000000)} Crore
        </td>
        <td className="text--md">{getFormattedDate(launchDate)}</td>
        {variant && (
          <td className="text--md">{this.safeDisplayValue(variant.type)}</td>
        )}
      </tr>
    );
  }

  private getReturnCell(value: any, key: string) {
    return (
      <td
        key={key}
        className={cx('text--md', {
          'font--green': value > 0,
          'font--red': value < 0,
        })}
      >
        {this.safeDisplayValue(value, '%')}
      </td>
    );
  }

  private getReturnsViewContent(fundData: any) {
    const { returns } = fundData;
    const keys = [
      'return10yr',
      'return5yr',
      'return3yr',
      'return1yr',
      'return6mth',
      'return3mth',
      'return1wk',
      'return1day',
    ];
    return (
      <tr className={styles['fund-row']}>
        <td className="text--md">{this.getFundNameColumnContent(fundData)}</td>
        {keys.map((key) => this.getReturnCell(returns[key], key))}
      </tr>
    );
  }

  private getOtherDetailsViewContent(fundData: any) {
    const { riskRatio } = fundData;
    const keys = ['alpha5yr', 'beta5yr', 'stdDev5yr', 'rsquared5yr'];
    return (
      <tr className={styles['fund-row']}>
        <td className="text--md">{this.getFundNameColumnContent(fundData)}</td>
        {keys.map((key) => (
          <td key={key} className="text--md">
            {this.safeDisplayValue(riskRatio[key])}
          </td>
        ))}
      </tr>
    );
  }

  private dismissModal = () => {
    this.setState({
      startFlow: false,
      showInvestmentDeniedModal: false,
    });
  };

  private getInvestmentDeniedModal = (allowed: boolean) => {
    return allowed ? (
      <SuggestedFundsModal
        dismissModal={this.dismissModal}
        isinCode={this.state.isinCode}
      />
    ) : null;
  };

  public render() {
    const {
      selectedTab,
      headers,
      fundsToCompare,
      selectedFund,
      startFlow,
      views,
      isLoading,
      showInvestmentDeniedModal,
    } = this.state;

    const investmentDeniedModal = this.getInvestmentDeniedModal(
      showInvestmentDeniedModal
    );

    return (
      <div className={cx(styles['compare-funds'], 'mb30')}>
        <div className={cx('row mt30', styles['pb0'])}>
          <div className="large-12 small-12 columns">
            <p className={cx(styles['search-title'], 'mb20')}>
              Compare over 300+ top-performing Mutual Funds
            </p>
          </div>

          <div className="columns large-12 small-12">
            <HomeSearch
              hideSearchLabel
              fullWidth
              handleFundClick={this.handleFundClick}
            />
          </div>
        </div>
        <div className="mt30 row">
          <div
            className={cx(
              styles['table-controls'],
              'pb10 columns large-12 small-12'
            )}
          >
            <div className="tabs-cotainer desktop-only">
              <Tabs
                align="horizontal"
                variant="filled"
                items={views.map((group) => ({
                  key: group.value,
                  label: group.label,
                }))}
                selected={selectedTab}
                switchTabs={this.switchTabs}
              />
            </div>
            <div className="filter-select mobile-only--display">
              <div className="columns small-6 mobile-only--display p10">
                Filter Views
              </div>
              <Field
                onChange={this.changeView}
                id="filter-view"
                name="filter-view"
                type="select"
                className={cx('small-6', styles['filter-select'])}
                options={views}
                value={selectedTab}
                hideEmpty
              />
            </div>
          </div>

          <div
            className={cx(
              styles['table-container'],
              'mb30 columns large-12 small-12'
            )}
          >
            <table className={styles.table}>
              <thead>
                <tr className={styles['fund-header']}>
                  {headers.map((header) => (
                    <th className="text--md font--dark-grey" key={header}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fundsToCompare.map((fund) => (
                  <Fragment key={fund.schemaMaster.isin}>
                    {selectedTab === 'quick'
                      ? this.getQuickViewContent(fund)
                      : null}
                    {selectedTab === 'details'
                      ? this.getDetailsViewContent(fund)
                      : null}
                    {selectedTab === 'returns'
                      ? this.getReturnsViewContent(fund)
                      : null}
                    {selectedTab === 'other'
                      ? this.getOtherDetailsViewContent(fund)
                      : null}
                  </Fragment>
                ))}
                {isLoading ? (
                  <tr className={styles['empty-row']}>
                    <td
                      className="text-center font--fadedgrey text--lg"
                      colSpan={headers.length}
                    >
                      <Loader />
                    </td>
                  </tr>
                ) : null}
                {!isLoading && !fundsToCompare.length ? (
                  <tr className={styles['empty-row']}>
                    <td
                      className="text-center font--fadedgrey text--lg"
                      colSpan={headers.length}
                    >
                      Search and add funds to compare.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
        {startFlow ? (
          <InvestFlowHandler
            {...this.props}
            selectedFund={selectedFund}
            onDismiss={this.dismissModal}
            sourceType="compare-funds"
            redirectUrl="/save/compare-funds"
          />
        ) : null}
        {investmentDeniedModal}
      </div>
    );
  }
}

export default withAppLayout(
  {
    title: 'Compare Mutual Funds Online',
    description:
      'ClearTax Invest fund comparison tool helps you compare between hundreds of mutual funds easily. Check out the tool and start your investment journey today!',
    hideHeaderSearch: true,
  },
  CompareFunds
);



// WEBPACK FOOTER //
// ./pages/compare-funds.tsx